import { memo } from "react";
import { useTranslation } from "react-i18next";
import { Bot, Pencil, Users } from "lucide-react";

import { AgentBadge } from "@/components/agent-badge";
import { NotificationIndicator } from "@/components/notification-indicator";
import { tagColorStyle, tagRailClass } from "@/components/kanban/KanbanCard";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import type { SessionNotificationState } from "@/hooks/use-notifications";
import type { TaskStatus } from "@/lib/project-types";
import { cn } from "@/lib/utils";
import { isMcpSession, type Session } from "@/lib/session";

export function McpSessionIndicator({ sessionId, className }: { sessionId: string; className?: string }) {
  const { t } = useTranslation("chat");
  const label = t("sidebar.mcpConversation");
  return (
    <span
      role="img"
      aria-label={label}
      title={label}
      data-testid={`mcp-session-icon-${sessionId}`}
      className={cn("inline-flex shrink-0 items-center justify-center text-muted-foreground", className)}
    >
      <Bot aria-hidden="true" className="size-3.5" />
    </span>
  );
}

export interface SessionRowProps {
  session: Session;
  displayTitle: string;
  isActive: boolean;
  agentName?: string;
  agentType?: "agent" | "team";
  notifState: SessionNotificationState | null;
  isPending: boolean;
  hasDraft: boolean;
  isTaskSession: boolean;
  taskStatus?: TaskStatus | null;
  isMobile: boolean;
  onSelect: (id: string) => void;
  /** Omit on surfaces, such as the agent hub, that do not offer rename. */
  onRenameOpen?: (id: string, title: string) => void;
  renameLabel: string;
  renameConversationLabel: string;
}

/**
 * A single conversation row shared by the session sidebar and agent hub.
 * The sidebar preserves its optional rename affordance; hub consumers simply
 * omit `onRenameOpen` to avoid expanding the hub scope with a context action.
 */
export const SessionRow = memo(function SessionRow({
  session,
  displayTitle,
  isActive,
  agentName,
  agentType,
  notifState,
  isPending,
  hasDraft,
  isTaskSession,
  taskStatus,
  isMobile,
  onSelect,
  onRenameOpen,
  renameLabel,
  renameConversationLabel,
}: SessionRowProps) {
  const { t } = useTranslation("chat");
  const mcpSession = isMcpSession(session);
  const firstTag = isTaskSession && Object.prototype.hasOwnProperty.call(session, "related_tasks")
    ? session.related_tasks?.[0]?.tags?.[0]
    : undefined;
  const tagLabel = firstTag?.color && firstTag.name
    ? t("sidebar.taskTag", { tag: firstTag.name })
    : undefined;
  return (
    <button
      type="button"
      data-testid={`session-row-${session.id}`}
      onClick={() => onSelect(session.id)}
      className={cn(
        "group relative flex w-full min-h-[36px] items-center gap-2 overflow-hidden rounded-md px-2 py-1.5 text-left text-sm transition-colors",
        isActive
          ? "bg-secondary text-secondary-foreground"
          : "hover:bg-muted",
      )}
    >
      {tagLabel && (
        <span
          role="img"
          aria-label={tagLabel}
          title={tagLabel}
          className={cn("absolute inset-y-1.5 left-0 rounded-full border-l-2", tagRailClass(firstTag?.color))}
          style={tagColorStyle(firstTag?.color)}
        />
      )}
      <NotificationIndicator
        state={notifState}
        // Voice-note preparation happens before a server-side run exists, so
        // it owns the sidebar status until normal run tracking takes over.
        statusOverride={isPending ? "pending" : undefined}
        hasDraft={hasDraft}
        taskSession={isTaskSession}
        taskStatus={taskStatus}
        testId={isTaskSession ? `task-session-icon-${session.id}` : undefined}
        idleIcon={mcpSession ? <McpSessionIndicator sessionId={session.id} /> : undefined}
      />
      <span className="min-w-0 flex-1 truncate">{displayTitle}</span>
      {session.is_shared_with_me && (
        <span
          className="inline-flex shrink-0 items-center gap-1 text-xs text-muted-foreground"
          aria-label={t("participation.sharedWithMe")}
          title={t("participation.sharedWithMeTooltip")}
        >
          <Users className="size-3" aria-hidden="true" />
          <span className="sr-only">{t("participation.sharedWithMe")}</span>
        </span>
      )}
      {/* Keep hover-only controls out of the flex flow so their expanded badge
          overlays the title instead of widening the session list item. */}
      <span
        className={cn(
          "absolute right-1 top-1/2 z-10 -translate-y-1/2 items-center gap-0.5 pl-3",
          isActive
            ? "bg-gradient-to-r from-transparent via-secondary to-secondary"
            : "bg-gradient-to-r from-transparent via-background to-background group-hover:via-muted group-hover:to-muted",
          isMobile && isActive ? "inline-flex" : "hidden group-hover:inline-flex",
        )}
      >
        <AgentBadge
          agentId={session.agent_id}
          agentName={agentName}
          type={agentType}
          size="sm"
        />
        {/* Edit button: hover-reveal on desktop, always visible on active rows
            on mobile so the user can rename without a hover state. */}
        {onRenameOpen && (isMobile ? isActive : true) && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  role="button"
                  tabIndex={-1}
                  aria-label={renameConversationLabel}
                  className="inline-flex size-6 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  onClick={(event) => {
                    event.stopPropagation();
                    onRenameOpen(session.id, session.title);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      event.stopPropagation();
                      onRenameOpen(session.id, session.title);
                    }
                  }}
                >
                  <Pencil className="size-3" />
                </span>
              </TooltipTrigger>
              <TooltipContent>{renameLabel}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </span>
    </button>
  );
});
